import { Polygon, Point2D, createPolygon } from './types';

interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Simple box clipping operations that preserve polygon vertices.
 * Unlike general polygon clipping libraries, this doesn't simplify or merge collinear edges.
 */
export class BoxClipper {
  private static readonly EPSILON = 1e-10;

  /**
   * Convert 4 corner points to a Box (axis-aligned bounding box)
   */
  static cornersToBox(corners: Point2D[]): Box {
    const xs = corners.map((c) => c.x);
    const ys = corners.map((c) => c.y);
    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys),
    };
  }

  /**
   * Check if a point is strictly inside a box (not on edges)
   */
  private static pointInBox(p: Point2D, box: Box): boolean {
    return p.x > box.minX + this.EPSILON && p.x < box.maxX - this.EPSILON && p.y > box.minY + this.EPSILON && p.y < box.maxY - this.EPSILON;
  }

  /**
   * Check if a point is on or inside a box
   */
  private static pointInOrOnBox(p: Point2D, box: Box): boolean {
    return p.x >= box.minX - this.EPSILON && p.x <= box.maxX + this.EPSILON && p.y >= box.minY - this.EPSILON && p.y <= box.maxY + this.EPSILON;
  }

  /**
   * Check if a point is exactly on a box edge (not inside, not outside)
   */
  private static pointOnBoxEdge(p: Point2D, box: Box): boolean {
    if (!this.pointInOrOnBox(p, box)) return false;
    if (this.pointInBox(p, box)) return false;
    return true;
  }

  /**
   * Check if a point is inside a polygon using ray casting
   */
  private static pointInPolygon(p: Point2D, polygon: Point2D[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x,
        yi = polygon[i].y;
      const xj = polygon[j].x,
        yj = polygon[j].y;
      if ((yi > p.y) !== (yj > p.y) && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * Clip a line segment against a box using Liang-Barsky algorithm
   * Returns intersection points sorted by parameter t (distance from p1)
   */
  private static clipSegmentToBox(p1: Point2D, p2: Point2D, box: Box): Point2D[] {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const intersections: { t: number; point: Point2D }[] = [];

    const isVertical = Math.abs(dx) <= this.EPSILON;
    const isHorizontal = Math.abs(dy) <= this.EPSILON;

    // Handle vertical segments (perpendicular to horizontal box edges)
    if (isVertical) {
      const x = p1.x;
      // Check if the segment is inside the box horizontally
      if (x >= box.minX - this.EPSILON && x <= box.maxX + this.EPSILON) {
        const minY = Math.min(p1.y, p2.y);
        const maxY = Math.max(p1.y, p2.y);

        // Check intersection with bottom edge
        if (box.minY >= minY - this.EPSILON && box.minY <= maxY + this.EPSILON) {
          const t = Math.abs(p2.y - p1.y) > this.EPSILON ? (box.minY - p1.y) / (p2.y - p1.y) : 0;
          if (t > this.EPSILON && t < 1 - this.EPSILON) {
            intersections.push({ t, point: { x, y: box.minY } });
          }
        }

        // Check intersection with top edge
        if (box.maxY >= minY - this.EPSILON && box.maxY <= maxY + this.EPSILON) {
          const t = Math.abs(p2.y - p1.y) > this.EPSILON ? (box.maxY - p1.y) / (p2.y - p1.y) : 1;
          if (t > this.EPSILON && t < 1 - this.EPSILON) {
            intersections.push({ t, point: { x, y: box.maxY } });
          }
        }
      }
    }
    // Handle horizontal segments (perpendicular to vertical box edges)
    else if (isHorizontal) {
      const y = p1.y;
      // Check if the segment is inside the box vertically
      if (y >= box.minY - this.EPSILON && y <= box.maxY + this.EPSILON) {
        const minX = Math.min(p1.x, p2.x);
        const maxX = Math.max(p1.x, p2.x);

        // Check intersection with left edge
        if (box.minX >= minX - this.EPSILON && box.minX <= maxX + this.EPSILON) {
          const t = Math.abs(p2.x - p1.x) > this.EPSILON ? (box.minX - p1.x) / (p2.x - p1.x) : 0;
          if (t > this.EPSILON && t < 1 - this.EPSILON) {
            intersections.push({ t, point: { x: box.minX, y } });
          }
        }

        // Check intersection with right edge
        if (box.maxX >= minX - this.EPSILON && box.maxX <= maxX + this.EPSILON) {
          const t = Math.abs(p2.x - p1.x) > this.EPSILON ? (box.maxX - p1.x) / (p2.x - p1.x) : 1;
          if (t > this.EPSILON && t < 1 - this.EPSILON) {
            intersections.push({ t, point: { x: box.maxX, y } });
          }
        }
      }
    }
    // Handle diagonal segments (standard case)
    else {
      // Left edge (x = minX)
      const t_left = (box.minX - p1.x) / dx;
      if (t_left > this.EPSILON && t_left < 1 - this.EPSILON) {
        const y = p1.y + t_left * dy;
        if (y >= box.minY - this.EPSILON && y <= box.maxY + this.EPSILON) {
          intersections.push({ t: t_left, point: { x: box.minX, y } });
        }
      }

      // Right edge (x = maxX)
      const t_right = (box.maxX - p1.x) / dx;
      if (t_right > this.EPSILON && t_right < 1 - this.EPSILON) {
        const y = p1.y + t_right * dy;
        if (y >= box.minY - this.EPSILON && y <= box.maxY + this.EPSILON) {
          intersections.push({ t: t_right, point: { x: box.maxX, y } });
        }
      }

      // Bottom edge (y = minY)
      const t_bottom = (box.minY - p1.y) / dy;
      if (t_bottom > this.EPSILON && t_bottom < 1 - this.EPSILON) {
        const x = p1.x + t_bottom * dx;
        if (x >= box.minX - this.EPSILON && x <= box.maxX + this.EPSILON) {
          intersections.push({ t: t_bottom, point: { x, y: box.minY } });
        }
      }

      // Top edge (y = maxY)
      const t_top = (box.maxY - p1.y) / dy;
      if (t_top > this.EPSILON && t_top < 1 - this.EPSILON) {
        const x = p1.x + t_top * dx;
        if (x >= box.minX - this.EPSILON && x <= box.maxX + this.EPSILON) {
          intersections.push({ t: t_top, point: { x, y: box.maxY } });
        }
      }
    }

    // Sort by t and return points
    intersections.sort((a, b) => a.t - b.t);

    // Remove duplicates
    const result: Point2D[] = [];
    for (const inter of intersections) {
      if (result.length === 0 || Math.abs(result[result.length - 1].x - inter.point.x) > this.EPSILON || Math.abs(result[result.length - 1].y - inter.point.y) > this.EPSILON) {
        result.push(inter.point);
      }
    }

    return result;
  }

  /**
   * Get the box corner by index (counter-clockwise order starting from bottom-left)
   * Index: 0=bottom-left, 1=bottom-right, 2=top-right, 3=top-left
   */
  private static getBoxCorner(box: Box, corner: number): Point2D {
    const idx = ((corner % 4) + 4) % 4;
    switch (idx) {
      case 0:
        return { x: box.minX, y: box.minY }; // bottom-left
      case 1:
        return { x: box.maxX, y: box.minY }; // bottom-right
      case 2:
        return { x: box.maxX, y: box.maxY }; // top-right
      case 3:
        return { x: box.minX, y: box.maxY }; // top-left
      default:
        return { x: box.minX, y: box.minY };
    }
  }

  /**
   * Get the box edge index for a point on the box boundary
   * Edge layout (counter-clockwise):
   *   - Edge 0: bottom (y=minY), from corner 0 to corner 1
   *   - Edge 1: right (x=maxX), from corner 1 to corner 2
   *   - Edge 2: top (y=maxY), from corner 2 to corner 3
   *   - Edge 3: left (x=minX), from corner 3 to corner 0
   * Returns: 0=bottom, 1=right, 2=top, 3=left, -1=not on edge
   */
  private static getBoxEdge(p: Point2D, box: Box): number {
    const onBottom = Math.abs(p.y - box.minY) < this.EPSILON;
    const onTop = Math.abs(p.y - box.maxY) < this.EPSILON;
    const onLeft = Math.abs(p.x - box.minX) < this.EPSILON;
    const onRight = Math.abs(p.x - box.maxX) < this.EPSILON;

    // Check edges in order, prioritizing by position
    if (onBottom && p.x >= box.minX - this.EPSILON && p.x <= box.maxX + this.EPSILON) return 0;
    if (onRight && p.y >= box.minY - this.EPSILON && p.y <= box.maxY + this.EPSILON) return 1;
    if (onTop && p.x >= box.minX - this.EPSILON && p.x <= box.maxX + this.EPSILON) return 2;
    if (onLeft && p.y >= box.minY - this.EPSILON && p.y <= box.maxY + this.EPSILON) return 3;
    return -1;
  }

  /**
   * Get the corner at the END of an edge when walking in the given direction
   * For CCW (step=+1): edge 0 ends at corner 1, edge 1 ends at corner 2, etc.
   * For CW (step=-1): edge 0 ends at corner 0, edge 1 ends at corner 1, etc.
   */
  private static getCornerAfterEdge(edge: number, clockwise: boolean): number {
    if (clockwise) {
      // Walking CW: edge 0->corner 0, edge 1->corner 1, edge 2->corner 2, edge 3->corner 3
      return edge;
    } else {
      // Walking CCW: edge 0->corner 1, edge 1->corner 2, edge 2->corner 3, edge 3->corner 0
      return (edge + 1) % 4;
    }
  }

  /**
   * Walk along box boundary from point p1 to point p2
   * clockwise=true: walk CW (for cutting/difference - trace inside of cut)
   * clockwise=false: walk CCW (for union - trace outside)
   * Returns the corner points to add between p1 and p2
   */
  private static walkBoxBoundary(p1: Point2D, p2: Point2D, box: Box, clockwise: boolean): Point2D[] {
    const edge1 = this.getBoxEdge(p1, box);
    const edge2 = this.getBoxEdge(p2, box);

    if (edge1 === -1 || edge2 === -1) return [];
    if (edge1 === edge2) return []; // Same edge, no corners needed

    const corners: Point2D[] = [];
    const step = clockwise ? -1 : 1; // CW = decreasing edge index, CCW = increasing

    let currentEdge = edge1;
    let iterations = 0;

    while (iterations < 5) {
      // Safety limit
      // Get the corner we pass when leaving this edge in our walking direction
      const cornerIdx = this.getCornerAfterEdge(currentEdge, clockwise);

      // Move to next edge
      const nextEdge = (((currentEdge + step) % 4) + 4) % 4;

      // If we've reached the destination edge, stop before adding its corner
      if (nextEdge === edge2) {
        // Add the corner between current and destination edge
        corners.push(this.getBoxCorner(box, cornerIdx));
        break;
      }

      // Add corner and continue
      corners.push(this.getBoxCorner(box, cornerIdx));
      currentEdge = nextEdge;
      iterations++;
    }

    return corners;
  }

  /**
   * Perturb polygon points that lie exactly on box edges to avoid degenerate cases.
   * Points on box edges are moved slightly inward or outward based on the polygon's
   * interior direction at that point.
   */
  private static perturbPolygonPoints(points: Point2D[], box: Box): Point2D[] {
    const PERTURB = this.EPSILON * 100; // Small but significant perturbation

    return points.map((p, i) => {
      if (!this.pointOnBoxEdge(p, box)) {
        return { ...p };
      }

      // Point is on box edge - determine which way to perturb based on neighbors
      const prev = points[(i - 1 + points.length) % points.length];
      const next = points[(i + 1) % points.length];

      // Check if neighbors are inside or outside the box
      const prevInside = this.pointInBox(prev, box);
      const nextInside = this.pointInBox(next, box);

      let dx = 0;
      let dy = 0;

      // Determine which edge we're on and perturb perpendicular to it
      const onLeft = Math.abs(p.x - box.minX) < this.EPSILON * 10;
      const onRight = Math.abs(p.x - box.maxX) < this.EPSILON * 10;
      const onBottom = Math.abs(p.y - box.minY) < this.EPSILON * 10;
      const onTop = Math.abs(p.y - box.maxY) < this.EPSILON * 10;

      if (onLeft) {
        // Perturb left (outside) if coming from/going to outside, else right (inside)
        dx = !prevInside && !nextInside ? -PERTURB : PERTURB;
      } else if (onRight) {
        dx = !prevInside && !nextInside ? PERTURB : -PERTURB;
      } else if (onBottom) {
        dy = !prevInside && !nextInside ? -PERTURB : PERTURB;
      } else if (onTop) {
        dy = !prevInside && !nextInside ? PERTURB : -PERTURB;
      }

      return { x: p.x + dx, y: p.y + dy };
    });
  }

  /**
   * Subtract a box from a polygon (difference operation)
   * Returns the polygon with the box cut out, preserving all original vertices.
   * When the box cuts entirely through the polygon, returns multiple separate polygons.
   */
  static difference(polygon: Polygon, boxCorners: Point2D[]): Polygon[] {
    const box = this.cornersToBox(boxCorners);
    // Perturb points that lie exactly on box edges to avoid degenerate cases
    const points = this.perturbPolygonPoints(polygon.points, box);

    if (points.length < 3) return [];

    // Check if polygon is completely inside box
    const allInside = points.every((p) => this.pointInOrOnBox(p, box));
    if (allInside) {
      // Check if box completely covers polygon
      const polyInBox = points.every((p) => this.pointInBox(p, box));
      if (polyInBox) {
        return []; // Polygon completely inside box
      }
    }

    // Check if box is completely outside polygon
    const boxPoints: Point2D[] = [
      { x: box.minX, y: box.minY },
      { x: box.maxX, y: box.minY },
      { x: box.maxX, y: box.maxY },
      { x: box.minX, y: box.maxY },
    ];

    // Check for any intersection
    let hasIntersection = false;
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      if (this.clipSegmentToBox(p1, p2, box).length > 0) {
        hasIntersection = true;
        break;
      }
    }

    const anyBoxCornerInPolygon = boxPoints.some((p) => this.pointInPolygon(p, points));
    const anyPolygonCornerInBox = points.some((p) => this.pointInBox(p, box));

    if (!hasIntersection && !anyBoxCornerInPolygon && !anyPolygonCornerInBox) {
      return [polygon]; // No intersection
    }

    // Collect all entry/exit events around the polygon
    interface ClipEvent {
      edgeIndex: number;
      t: number; // Parameter along the edge (0-1)
      point: Point2D;
      isEntry: boolean; // true = entering box, false = exiting box
    }

    const events: ClipEvent[] = [];

    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p1Inside = this.pointInBox(p1, box);
      const p2Inside = this.pointInBox(p2, box);
      const p1OnEdge = this.pointOnBoxEdge(p1, box);
      const p2OnEdge = this.pointOnBoxEdge(p2, box);

      const intersections = this.clipSegmentToBox(p1, p2, box);

      // Handle case where p1 is exactly on box edge
      if (p1OnEdge) {
        // Look at previous point to determine if this is entry or exit
        const prevIdx = (i - 1 + points.length) % points.length;
        const prevPoint = points[prevIdx];
        const prevInside = this.pointInBox(prevPoint, box);

        if (!prevInside && (p2Inside || p2OnEdge)) {
          // Coming from outside, going inside or along edge - this is an entry
          events.push({
            edgeIndex: i,
            t: 0,
            point: { ...p1 },
            isEntry: true,
          });
        } else if ((prevInside || this.pointOnBoxEdge(prevPoint, box)) && !p2Inside && !p2OnEdge) {
          // Coming from inside or edge, going outside - this is an exit
          events.push({
            edgeIndex: i,
            t: 0,
            point: { ...p1 },
            isEntry: false,
          });
        }
      }

      if (intersections.length > 0) {
        if (!p1Inside && !p1OnEdge && (p2Inside || p2OnEdge)) {
          // Entering the box
          events.push({
            edgeIndex: i,
            t: this.getParameterOnSegment(p1, p2, intersections[0]),
            point: intersections[0],
            isEntry: true,
          });
        } else if ((p1Inside || p1OnEdge) && !p2Inside && !p2OnEdge) {
          // Exiting the box (only if not already handled by p1OnEdge case)
          if (!p1OnEdge) {
            events.push({
              edgeIndex: i,
              t: this.getParameterOnSegment(p1, p2, intersections[intersections.length - 1]),
              point: intersections[intersections.length - 1],
              isEntry: false,
            });
          }
        } else if (!p1Inside && !p1OnEdge && !p2Inside && !p2OnEdge && intersections.length >= 2) {
          // Edge crosses through the box (enters and exits on same edge)
          events.push({
            edgeIndex: i,
            t: this.getParameterOnSegment(p1, p2, intersections[0]),
            point: intersections[0],
            isEntry: true,
          });
          events.push({
            edgeIndex: i,
            t: this.getParameterOnSegment(p1, p2, intersections[1]),
            point: intersections[1],
            isEntry: false,
          });
        }
      }
    }

    // If no events, the box is entirely inside the polygon - create a hole
    if (events.length === 0) {
      if (anyBoxCornerInPolygon) {
        // Box is entirely inside polygon - this creates a hole, not handled by simple clipping
        // Return original polygon (the hole case should be handled separately)
        return [polygon];
      }
      return [polygon];
    }

    // Sort events by edge index, then by t parameter
    events.sort((a, b) => {
      if (a.edgeIndex !== b.edgeIndex) return a.edgeIndex - b.edgeIndex;
      return a.t - b.t;
    });

    // Events should alternate: exit, entry, exit, entry, ...
    // Each exit-entry pair defines a separate polygon region
    // Find exit events and pair them with the next entry event

    const resultPolygons: Polygon[] = [];

    // Build polygons by walking from each exit point to the next entry point
    for (let eventIdx = 0; eventIdx < events.length; eventIdx++) {
      const exitEvent = events[eventIdx];
      if (exitEvent.isEntry) continue; // Skip entry events, we start from exits

      const entryEvent = events[(eventIdx + 1) % events.length];

      // Build a polygon from exitEvent to entryEvent
      const polygonPoints: Point2D[] = [];

      // Start with exit point
      polygonPoints.push({ ...exitEvent.point });

      // Walk along polygon edges from exit to entry
      let currentEdge = exitEvent.edgeIndex;
      const targetEdge = entryEvent.edgeIndex;

      // Add vertices along the way
      let iterations = 0;
      const maxIterations = points.length + 2;

      while (iterations < maxIterations) {
        // Move to next vertex
        const nextVertex = (currentEdge + 1) % points.length;

        // Check if we've reached the target edge
        if (currentEdge === targetEdge && entryEvent.edgeIndex === exitEvent.edgeIndex) {
          // Same edge - just add entry point if t values are different
          if (entryEvent.t > exitEvent.t) {
            // Entry is after exit on same edge
            break;
          }
        }

        // Add the next vertex if it's outside the box
        const vertex = points[nextVertex];
        if (!this.pointInBox(vertex, box)) {
          polygonPoints.push({ x: vertex.x, y: vertex.y });
        }

        currentEdge = nextVertex;

        // Check if we've reached the entry edge
        if (currentEdge === targetEdge) {
          break;
        }

        iterations++;
      }

      // Add entry point
      polygonPoints.push({ ...entryEvent.point });

      // Walk box boundary from entry to exit (clockwise for difference)
      const boxCornerPoints = this.walkBoxBoundary(entryEvent.point, exitEvent.point, box, true);
      for (const corner of boxCornerPoints) {
        polygonPoints.push(corner);
      }

      // Clean up and validate
      const cleaned = this.removeDuplicatePoints(polygonPoints);

      if (cleaned.length >= 3) {
        const resultPolygon = createPolygon(cleaned, polygon.isHole);
        // Distribute children to appropriate result polygons
        if (polygon.children) {
          const childrenForThisPolygon = polygon.children.filter((child) => {
            // Check if child centroid is inside this result polygon
            const centroid = this.getPolygonCentroid(child.points);
            return this.pointInPolygon(centroid, cleaned);
          });
          if (childrenForThisPolygon.length > 0) {
            resultPolygon.children = childrenForThisPolygon;
          }
        }
        resultPolygons.push(resultPolygon);
      }
    }

    if (resultPolygons.length === 0) {
      return [polygon]; // Failed to clip, return original
    }

    return resultPolygons;
  }

  /**
   * Get the parameter t (0-1) of a point on a line segment
   */
  private static getParameterOnSegment(p1: Point2D, p2: Point2D, point: Point2D): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;

    if (Math.abs(dx) > Math.abs(dy)) {
      return (point.x - p1.x) / dx;
    } else if (Math.abs(dy) > this.EPSILON) {
      return (point.y - p1.y) / dy;
    }
    return 0;
  }

  /**
   * Calculate the centroid of a polygon
   */
  private static getPolygonCentroid(points: Point2D[]): Point2D {
    let cx = 0;
    let cy = 0;
    for (const p of points) {
      cx += p.x;
      cy += p.y;
    }
    return { x: cx / points.length, y: cy / points.length };
  }

  /**
   * Add a box to a polygon (union operation)
   * Returns the merged polygon, preserving all original vertices where possible
   */
  static union(polygon: Polygon, boxCorners: Point2D[]): Polygon[] {
    const box = this.cornersToBox(boxCorners);
    // Perturb points that lie exactly on box edges to avoid degenerate cases
    const points = this.perturbPolygonPoints(polygon.points, box);

    if (points.length < 3) {
      return [
        createPolygon(
          [
            { x: box.minX, y: box.minY },
            { x: box.maxX, y: box.minY },
            { x: box.maxX, y: box.maxY },
            { x: box.minX, y: box.maxY },
          ],
          false,
        ),
      ];
    }

    const boxPoints: Point2D[] = [
      { x: box.minX, y: box.minY },
      { x: box.maxX, y: box.minY },
      { x: box.maxX, y: box.maxY },
      { x: box.minX, y: box.maxY },
    ];

    // Check if box is completely inside polygon
    const allBoxInPolygon = boxPoints.every((p) => this.pointInPolygon(p, points));
    if (allBoxInPolygon) {
      return [polygon]; // Box inside polygon, no change
    }

    // Check if polygon is completely inside box
    const allPolyInBox = points.every((p) => this.pointInBox(p, box));
    if (allPolyInBox) {
      // Polygon is fully contained in box - just return the polygon unchanged
      // (union with containing box doesn't change the visible result)
      return [polygon];
    }

    // Check for intersection
    let hasIntersection = false;
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      if (this.clipSegmentToBox(p1, p2, box).length > 0) {
        hasIntersection = true;
        break;
      }
    }

    const anyBoxCornerInPolygon = boxPoints.some((p) => this.pointInPolygon(p, points));
    const anyPolygonCornerInBox = points.some((p) => this.pointInBox(p, box));

    if (!hasIntersection && !anyBoxCornerInPolygon && !anyPolygonCornerInBox) {
      // No intersection at all - box is completely outside polygon, return unchanged
      return [polygon];
    }

    // Build union by walking both boundaries
    const result: Point2D[] = [];
    let entryPoint: Point2D | null = null;

    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p1Inside = this.pointInBox(p1, box);
      const p2Inside = this.pointInBox(p2, box);
      const p1OnEdge = this.pointOnBoxEdge(p1, box);
      const p2OnEdge = this.pointOnBoxEdge(p2, box);

      const intersections = this.clipSegmentToBox(p1, p2, box);

      if (!p1Inside && !p1OnEdge) {
        // Point is strictly outside box, add it to union
        result.push({ x: p1.x, y: p1.y });
      } else if (p1OnEdge) {
        // Point is on box edge - add it but also handle entry/exit
        result.push({ x: p1.x, y: p1.y });

        // Check if this is an entry point (coming from outside, going inside)
        const prevIdx = (i - 1 + points.length) % points.length;
        const prevPoint = points[prevIdx];
        const prevInside = this.pointInBox(prevPoint, box);
        const prevOnEdge = this.pointOnBoxEdge(prevPoint, box);

        if (!prevInside && !prevOnEdge && (p2Inside || p2OnEdge)) {
          entryPoint = { ...p1 };
        } else if ((prevInside || prevOnEdge) && !p2Inside && !p2OnEdge) {
          // Exiting at this point
          if (entryPoint) {
            const corners = this.walkBoxBoundary(entryPoint, p1, box, false);
            for (const corner of corners) {
              if (!this.pointInPolygon(corner, points)) {
                result.push(corner);
              }
            }
          }
          entryPoint = null;
        }
      }

      if (intersections.length > 0) {
        if (!p1Inside && !p1OnEdge && (p2Inside || p2OnEdge)) {
          // Entering the box - add intersection and record entry point
          result.push({ ...intersections[0] });
          entryPoint = intersections[0];
        } else if (p1Inside && !p2Inside && !p2OnEdge) {
          // Exiting the box (from strictly inside)
          const exitPoint = intersections[intersections.length - 1];

          // For union, walk the OUTSIDE of box (counter-clockwise)
          if (entryPoint) {
            const corners = this.walkBoxBoundary(entryPoint, exitPoint, box, false);
            for (const corner of corners) {
              // Only add box corners that are outside the polygon
              if (!this.pointInPolygon(corner, points)) {
                result.push(corner);
              }
            }
          }
          result.push({ ...exitPoint });
          entryPoint = null;
        } else if (!p1Inside && !p1OnEdge && !p2Inside && !p2OnEdge && intersections.length >= 2) {
          // Edge passes through box
          result.push({ ...intersections[0] });
          const corners = this.walkBoxBoundary(intersections[0], intersections[1], box, false);
          for (const corner of corners) {
            if (!this.pointInPolygon(corner, points)) {
              result.push(corner);
            }
          }
          result.push({ ...intersections[1] });
        }
      }
    }

    const cleaned = this.removeDuplicatePoints(result);

    if (cleaned.length < 3) {
      return [polygon];
    }

    const resultPolygon = createPolygon(cleaned, polygon.isHole);

    // Process children (holes) - remove or shrink holes that overlap with the box
    if (polygon.children && polygon.children.length > 0) {
      const remainingChildren: Polygon[] = [];

      for (const child of polygon.children) {
        // Check if box completely contains the hole (fills it entirely)
        const holePoints = child.points;
        const allHolePointsInBox = holePoints.every((p) => this.pointInOrOnBox(p, box));

        if (allHolePointsInBox) {
          // Box completely covers this hole - the hole is filled, remove it
          continue;
        }

        // Check if box overlaps with the hole
        let hasOverlap = false;
        for (let i = 0; i < holePoints.length; i++) {
          const p1 = holePoints[i];
          const p2 = holePoints[(i + 1) % holePoints.length];
          if (this.clipSegmentToBox(p1, p2, box).length > 0) {
            hasOverlap = true;
            break;
          }
        }

        const anyHolePointInBox = holePoints.some((p) => this.pointInBox(p, box));
        const anyBoxPointInHole = boxPoints.some((p) => this.pointInPolygon(p, holePoints));

        if (!hasOverlap && !anyHolePointInBox && !anyBoxPointInHole) {
          // No overlap - keep the hole as is
          remainingChildren.push(child);
        } else {
          // Box partially overlaps with hole - shrink the hole by unioning with the box
          // For a hole, union with box = subtract the box from the hole area
          // This means the hole gets smaller where the box covers it
          const shrunkHoles = this.difference(child, boxCorners);
          for (const shrunkHole of shrunkHoles) {
            // Only keep if it still has meaningful area
            if (shrunkHole.points.length >= 3) {
              remainingChildren.push(shrunkHole);
            }
          }
        }
      }

      if (remainingChildren.length > 0) {
        resultPolygon.children = remainingChildren;
      }
    }

    return [resultPolygon];
  }

  /**
   * Remove duplicate consecutive points
   */
  private static removeDuplicatePoints(points: Point2D[]): Point2D[] {
    if (points.length === 0) return [];

    const result: Point2D[] = [points[0]];
    for (let i = 1; i < points.length; i++) {
      const prev = result[result.length - 1];
      const curr = points[i];
      if (Math.abs(curr.x - prev.x) > this.EPSILON || Math.abs(curr.y - prev.y) > this.EPSILON) {
        result.push(curr);
      }
    }

    // Check first and last
    if (result.length > 1) {
      const first = result[0];
      const last = result[result.length - 1];
      if (Math.abs(first.x - last.x) <= this.EPSILON && Math.abs(first.y - last.y) <= this.EPSILON) {
        result.pop();
      }
    }

    return result;
  }
}
